// components/SortableList.jsx
'use client';

import React, { useState } from 'react';
import { useSortable, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
    PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
    useDroppable, DndContext, DragOverlay, closestCenter,
} from '@dnd-kit/core';
import { MdDragIndicator } from 'react-icons/md';

/**
 * PointerSensor restricted to mouse/pen. Without this, iOS routes touches
 * through BOTH PointerSensor and TouchSensor and the stricter one wins
 * unpredictably; touch must belong to TouchSensor alone.
 */
class MouseSensor extends PointerSensor {
    static activators = [{
        eventName: 'onPointerDown',
        handler: ({ nativeEvent }) => nativeEvent.pointerType !== 'touch',
    }];
}

/**
 * The sensor set for handle-dragged lists.
 *
 * TOUCH ACTIVATES IMMEDIATELY — no delay, no tolerance. The first release
 * required a 150ms hold within 8px, which the simulator passed (synthetic
 * touches are perfectly still) and real fingers failed: fingertip jitter
 * during the hold exceeded 8px, the activation cancelled, and the page
 * scrolled instead — "I could not get anything to move on my cell phone"
 * (owner, 2026-08-19). A drag HANDLE needs no disambiguation window at all:
 * it does nothing else, and its `touch-action: none` already keeps scroll
 * from claiming touches that start on it. This is how the OS's own reorder
 * handles behave.
 *
 * Mouse keeps a small distance threshold purely so stray clicks on the
 * handle are not registered as zero-length drags.
 */
export function useListDndSensors() {
    return useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor),
        // The press-to-activate path the handle's role="button" promises:
        // focus the grip, Enter lifts, arrows move, Enter drops, Esc cancels.
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
}

/**
 * One hook site for every draggable row/card in a master list (PDF Multi, PDF/Weblink List, …). Render-prop
 * so the row markup stays inline where the page's helpers are in scope.
 *
 * `container` rides along in the sortable data: 'root' for the top-level lane,
 * a folder id for that folder's children. onListDragEnd only reorders when
 * active and over share a container — dragging BETWEEN lanes is deliberately
 * out of the pilot (the Folder field in the item modals is the mover).
 */
export function Sortable({ id, container, kind = 'item', expanded = false, children }) {
    const {
        attributes, listeners, setNodeRef, isDragging,
    } = useSortable({ id, data: { container, kind, expanded } });
    // Deliberately NO sortable transform: rows must stay PUT during a drag.
    // The DragOverlay ghost and the blue insertion line are the preview, and
    // both are computed against the rows' static rects. Applying
    // verticalListSortingStrategy's sibling-shifting transform on top moved
    // every row a slot away from the rect the line was computed from, so the
    // line pointed one insertion slot away from where the drop actually
    // landed (PM, 2026-08-23). One preview, one geometry.
    return children({ ref: setNodeRef, style: undefined, isDragging, handle: { ...attributes, ...listeners } });
}

/**
 * The grip a row is dragged by — same affordance as the owner's staffing
 * project. Listeners live ONLY here, so taps on the row still open things and
 * a swipe over the list still scrolls the page; `touch-none` keeps iOS from
 * claiming the gesture for scrolling once the finger lands on the grip.
 */
export function DragHandle({ handle, color }) {
    // `color`: an explicit icon colour for rows drawn on a user-chosen
    // background (section headers) — the grey default vanishes on a dark
    // section. Hover then tints with a translucent wash instead of grey.
    return (
        <button type="button" {...handle}
                style={color ? { color } : undefined}
                className={`cursor-grab active:cursor-grabbing touch-none select-none rounded p-2.5 -m-1 transition
                            ${color ? 'hover:bg-black/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                title="Drag to reorder" aria-label="Drag to reorder">
            {/* p-2.5 + 16px icon ≈ a 36px finger target (was 24px); the
                negative margin keeps the visual footprint in the row. */}
            <MdDragIndicator className="w-4 h-4" />
        </button>
    );
}


export { MdDragIndicator };

/**
 * The drop target an EMPTY expanded folder shows. Without it there is no row
 * to drop onto, so an item could not be dragged into an empty folder at all
 * (owner, on-device, 2026-08-19). Registers as a droppable in the folder's
 * lane with a synthetic id no child matches — applyListDrag lands the item as
 * the folder's first child.
 *
 * Render prop so each caller keeps its own markup (table row vs card).
 */
export function EmptyFolderDrop({ folderId, children }) {
    const { setNodeRef, isOver } = useDroppable({
        id: `empty:${folderId}`,
        data: { container: folderId, kind: 'empty' },
    });
    return children({ ref: setNodeRef, isOver });
}

/**
 * Where the pointer sits on the hovered row — AND what is being dragged —
 * decides what a drop MEANS:
 *
 *   item over item row      top half → 'before', bottom half → 'after'
 *   item over OPEN folder   top third → 'before', bottom third → 'after',
 *                           middle third → 'into' (appended at the end).
 *                           A third, not half: with half, aiming BETWEEN two
 *                           folders dropped the file into one of them too
 *                           easily (owner, 2026-08-23).
 *   item over CLOSED folder plain half/half reorder slot — no 'into'. Closed-
 *                           folder insertion shipped in 15.19 and was removed
 *                           on PM review (2026-08-24): too easy to feed a
 *                           folder when aiming beside it. The empty zone and
 *                           open-folder header remain the ways in by drag.
 *   item over empty zone    always 'into'
 *   item over its OWN folder's header → 'after' (pop it out, just below
 *                           the folder) — the natural pull-out gesture,
 *                           which used to compute as a refused no-op
 *   FOLDER over anything    'into' does not exist — folders never nest, so a
 *                           folder row is a plain half/half reorder target and
 *                           an empty zone offers nothing (null). Offering
 *                           'into' here made applyListDrag refuse the drop,
 *                           and since a folder-dense list is nearly all
 *                           middle-of-folder, folders looked immovable.
 *
 * Computed from the dragged element's live rect (its vertical centre) against
 * the hovered row's rect, so touch and mouse behave identically. The same
 * function feeds both the insertion line (onDragOver) and the drop
 * (onDragEnd), so the line can never promise something the drop won't do.
 */
export function dropIntent({ active, over }) {
    if (!over) return null;
    const overKind = over.data?.current?.kind;
    const activeIsFolder = active?.data?.current?.kind === 'folder';
    if (overKind === 'empty') return activeIsFolder ? null : 'into';
    const rect = over.rect;
    const moving = active?.rect?.current?.translated || active?.rect?.current?.initial;
    if (!rect || !moving || !rect.height) return 'before';
    const ratio = (moving.top + moving.height / 2 - rect.top) / rect.height;
    if (overKind === 'folder' && !activeIsFolder) {
        // An item dragged onto the header of the folder it is ALREADY in:
        // "into" would be a no-op, and this is exactly the gesture people
        // reach for to pull an item OUT — so it means "place just below this
        // folder in the main list" at any height (owner, 2026-08-23).
        if (active?.data?.current?.container === over.id) return 'after';
        // Closed folders are plain reorder slots (see doc block).
        if (!over.data?.current?.expanded) return ratio < 0.5 ? 'before' : 'after';
        if (ratio < 1 / 3) return 'before';
        if (ratio > 2 / 3) return 'after';
        return 'into';
    }
    return ratio < 0.5 ? 'before' : 'after';
}

/**
 * Tracks the in-flight drag so rows can draw the insertion line. Spread
 * `handlers` onto the DndContext (merge your own onDragEnd after `clear`).
 *
 * `drag` is null when nothing is lifted; otherwise
 * { activeId, from, overId, to, position }.
 */
export function useDragOverState() {
    const [drag, setDrag] = useState(null);
    const recordIntent = (event) => setDrag(d => {
        if (!d) return d;
        const overId = event.over?.id ?? null;
        const to = event.over?.data?.current?.container ?? null;
        const position = dropIntent(event);
        if (d.overId === overId && d.to === to && d.position === position) return d;
        return { ...d, overId, to, position };
    });
    const handlers = {
        onDragStart: ({ active, activatorEvent }) => setDrag({
            activeId: active.id,
            from: active.data.current?.container ?? null,
            overId: null,
            to: null,
            position: null,
            // Touch drags float the ghost ABOVE the fingertip — under it, the
            // finger hides the caption entirely (owner, 2026-08-23). Purely
            // visual: intent math reads the logical drag rect, not the ghost.
            touch: !!activatorEvent?.type?.startsWith?.('touch'),
        }),
        onDragOver: recordIntent,
        // dnd-kit fires onDragOver only when the hovered ROW changes, so with
        // it alone the intent froze at row-entry: entering the first row from
        // below recorded 'after' and its top half could never record 'before'
        // — the very top and bottom of the list were unreachable (PM,
        // 2026-08-24). onDragMove tracks every movement; the identity
        // bail-out keeps the per-move setState from re-rendering the page
        // unless the intent actually changed.
        onDragMove: recordIntent,
        onDragCancel: () => setDrag(null),
        clear: () => setDrag(null),
    };
    return { drag, handlers };
}

/**
 * Which indicator row `id` should draw: 'above' | 'below' | 'into' | null.
 * Straight from the recorded intent, so the line and the drop agree.
 */
export function dropEdgeFor(drag, id) {
    if (!drag || drag.overId !== id || !drag.position) return null;
    if (drag.position === 'into') return 'into';
    return drag.position === 'before' ? 'above' : 'below';
}

/**
 * Class names for the indicator. A CLASS, not an inline box-shadow: browsers
 * do not paint box-shadow on <tr>, which is why the line showed on the mobile
 * cards and never on the desktop table (PM feedback 2026-08-22). The rules in
 * globals.css paint on the row's cells for tables and on the element itself
 * for cards.
 */
export function dropEdgeClass(edge) {
    if (edge === 'above') return 'drop-edge-above';
    if (edge === 'below') return 'drop-edge-below';
    if (edge === 'into') return 'drop-edge-into';
    return '';
}

/**
 * What rides under the pointer while dragging — a compact stand-in rendered
 * inside dnd-kit's <DragOverlay>. Without an overlay the SOURCE row is what
 * moves, and when it hovers a droppable outside its own sortable lane (an
 * empty folder's zone) its transform resets and it jumps away — "the item
 * disappears and just shows Drop here" (PM feedback 2026-08-22).
 */
export function DragGhost({ icon, title, kind, lifted = false }) {
    return (
        <div className={`rounded-lg border border-yellow-400 bg-white shadow-xl
                        px-3 py-2 cursor-grabbing select-none
                        ${lifted ? '-translate-y-16' : ''}`}>
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <MdDragIndicator className="w-4 h-4 text-gray-400 shrink-0" />
                {icon && <span className="shrink-0">{icon}</span>}
                <span className="truncate max-w-[16rem]">{title}</span>
                {kind === 'folder' && <span className="text-xs font-normal text-gray-400">folder</span>}
            </div>
        </div>
    );
}

/**
 * The position a DROP must use: the RECORDED intent from the last onDragOver
 * — the exact thing the blue line and the ghost caption were showing when
 * the user released. Recomputing dropIntent at drag end is wrong: dnd-kit
 * can have already dropped the ghost's translated rect by then, and the
 * fallback (the item's ORIGINAL rect) yields a position from nowhere — the
 * screen said "Above X" and the drop went into X (owner, 2026-08-23). The
 * promise on screen IS the drop, so the drop reads the promise.
 *
 * Null (including a recorded null for this over-target) means REFUSE the
 * drop — never default it. Recomputes only if the end-time over was never
 * recorded (a same-tick over-change; rare).
 */
/**
 * A whole flat lane in one component — for the many small in-form lists
 * (contacts, tags, extra fields…) that were arrow-reordered.
 *
 * It OWNS the list container (pass the container's className / element via
 * `as` + `containerProps`) because dnd-kit renders hidden accessibility nodes
 * inside its context: leaving those as siblings of the rows puts them inside
 * a `divide-y` parent, where they collect stray divider borders.
 *
 * `items` are reordered in place and handed back to `onReorder`; ids are
 * position-derived by default, which is safe because rows stay static during a
 * drag (see the invariant list in the spec).
 */
export function FlatDragList({
    items, onReorder, titleOf = () => '', idOf,
    as: Container = 'div', containerProps = {}, children,
}) {
    const sensors = useListDndSensors();
    const { drag, handlers } = useDragOverState();
    const entries = (items || []).map((item, index) => ({
        id: idOf ? idOf(item, index) : `row:${index}`, item, index,
    }));

    // dnd-kit's hidden accessibility nodes (`DndDescribedBy…` + the live
    // region) render as SIBLINGS of the container. For a div lane that is
    // merely a styling nuisance (the divide-y note above) — but when the
    // container is a <tbody>, a sibling <div> becomes a direct child of
    // <table>, which is invalid HTML and a React 19 hydration error
    // (phonebook ContactTable, 2026-09-01). Portal them to <body> instead:
    // announcements still work, and no host element can be a wrong parent.
    // Lazy useState, not useEffect: the value must be right on the FIRST
    // client render or the invalid node exists for a frame anyway.
    const [a11yContainer] = useState(
        () => (typeof document === 'undefined' ? undefined : document.body));

    return (
        <DndContext
            accessibility={{ container: a11yContainer }}
            sensors={sensors}
            collisionDetection={closestCenter}
            autoScroll={{ threshold: { x: 0.2, y: 0.3 } }}
            onDragStart={handlers.onDragStart}
            onDragOver={handlers.onDragOver}
            onDragMove={handlers.onDragMove}
            onDragCancel={handlers.onDragCancel}
            onDragEnd={(event) => {
                handlers.clear();
                const next = applyFlatDrag(entries, drag, event);
                if (next) onReorder(next.map(e => e.item));
            }}
        >
            <Container {...containerProps}>
                {entries.map(({ id, item, index }) => (
                    <Sortable key={id} id={id} container="root">
                        {({ ref, style, handle, isDragging }) => children({
                            item, index, ref, style, handle, isDragging,
                            edgeClass: dropEdgeClass(dropEdgeFor(drag, id)),
                        })}
                    </Sortable>
                ))}
            </Container>
            <DragOverlay dropAnimation={null}>
                {drag ? <DragGhost lifted={!!drag.touch} title={titleOf(entries.find(e => e.id === drag.activeId)?.item)} /> : null}
            </DragOverlay>
        </DndContext>
    );
}

/**
 * ONE FLAT LANE (Tier 3 lists — no folders): apply a drag to `items` using
 * the recorded intent and return the reordered array, or null when nothing
 * changes. 'into' cannot occur (no row registers kind 'folder'), so every
 * drop is a plain before/after splice. Renumbering is the caller's business —
 * flat lists have no combined-mode concerns.
 */
export function applyFlatDrag(items, drag, { active, over }, idOf = (x) => x.id) {
    if (!over || !active || active.id === over.id) return null;
    const position = dropPositionFor(drag, { active, over });
    if (position !== 'before' && position !== 'after') return null;
    const fromIdx = items.findIndex(x => idOf(x) === active.id);
    if (fromIdx < 0 || !items.some(x => idOf(x) === over.id)) return null;
    const list = [...items];
    const [moved] = list.splice(fromIdx, 1);
    const overIdx = list.findIndex(x => idOf(x) === over.id);
    list.splice(overIdx + (position === 'after' ? 1 : 0), 0, moved);
    return list;
}

export function dropPositionFor(drag, { active, over }) {
    if (!over) return null;
    if (drag && drag.overId === over.id) return drag.position;
    return dropIntent({ active, over });
}

